import {
  Breadcrumb,
  BreadcrumbIcon,
  BreadcrumbLink,
  BreadcrumbPage,
  Button,
  Dropdown,
  DropdownItem,
  DropdownItemContent,
  DropdownTitle,
  IconButton,
  Tooltip,
} from '@ads/components-react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowsRightLeftIcon,
  CalendarDaysIcon,
  EllipsisVerticalIcon,
  PencilSquareIcon,
  PlusIcon,
  UserIcon,
  UserMinusIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { ContractHeaderAction } from '../../../types/parcels';

export interface IndividualPaymentParcelsTemplateProps {
  /** Nome do aluno — segmento atual do breadcrumb. */
  readonly studentName: string;
  /** Curso do contrato, exibido como subtítulo. */
  readonly courseName?: string | null | undefined;
  /** `false` desabilita "Editar pessoa" (o header não trouxe `personId`). */
  readonly canEditPerson: boolean;
  /**
   * Ações de "Mais ações" que ficam inertes. Usado pelas ações em lote quando o
   * conjunto derivado é vazio: sem parcela elegível não há payload a enviar.
   */
  readonly disabledActions?: readonly ContractHeaderAction[];
  /**
   * Ações que não são renderizadas — **da régua inteira**, não só de "Mais
   * ações": as regras do legado escondem por papel (`editPayment` e `addParcel`
   * não são de secretária) e por forma de pagamento (`downloadBooklet` só em
   * boleto comum), e essas três são botões diretos.
   *
   * Distinto de `disabledActions`, e a diferença é de significado: inerte diz
   * "não há o que fazer agora" (nenhuma parcela elegível no lote); ausente diz
   * "isso não se aplica a este contrato ou a este operador".
   */
  readonly hiddenActions?: readonly ContractHeaderAction[];
  readonly onAction: (action: ContractHeaderAction) => void;
  readonly children: ReactNode;
}

/** Operações de nível-contrato que a API oferece e a tela legada não expõe. */
const MORE_ACTIONS = [
  {
    action: 'cancelOpenParcels',
    labelKey: 'parcels.headerActions.cancelOpenParcels',
    icon: XCircleIcon,
    isDestructive: true,
  },
  {
    action: 'openCancelledParcels',
    labelKey: 'parcels.headerActions.openCancelledParcels',
    icon: ArrowPathIcon,
  },
  {
    action: 'resetExtendedExpiration',
    labelKey: 'parcels.headerActions.resetExtendedExpiration',
    icon: CalendarDaysIcon,
  },
  {
    action: 'renegotiation',
    labelKey: 'parcels.headerActions.renegotiation',
    icon: ArrowsRightLeftIcon,
  },
  {
    action: 'inactivateFinancial',
    labelKey: 'parcels.headerActions.inactivateFinancial',
    icon: UserMinusIcon,
    isDestructive: true,
  },
] as const satisfies readonly {
  readonly action: ContractHeaderAction;
  readonly labelKey: string;
  readonly icon: typeof XCircleIcon;
  readonly isDestructive?: boolean;
}[];

/**
 * Layout da tela de visualização de parcelas: breadcrumb, page header com a
 * régua de ações e o slot de conteúdo (cabeçalho do contrato e tabela).
 *
 * A régua segue o frame `OzPIl`: três ações secundárias, "Adicionar parcela"
 * como primária e as cinco operações de nível-contrato em "Mais ações".
 *
 * "Editar pessoa" navega; cancelar parcelas abertas, reabrir canceladas e
 * inativar financeiro abrem um modal de confirmação; as demais informam que
 * ainda não estão disponíveis, no mesmo padrão da listagem.
 */
export function IndividualPaymentParcelsTemplate({
  studentName,
  courseName,
  canEditPerson,
  disabledActions = [],
  hiddenActions = [],
  onAction,
  children,
}: IndividualPaymentParcelsTemplateProps) {
  const { t } = useTranslation('individualPayment');

  const moreActions = MORE_ACTIONS.filter(item => !hiddenActions.includes(item.action));
  const isVisible = (action: ContractHeaderAction) => !hiddenActions.includes(action);

  return (
    <div className="flex flex-col gap-xs">
      <div className="flex flex-col gap-nano">
        <Breadcrumb>
          <BreadcrumbLink href="/">
            <Link to="/">
              <BreadcrumbIcon />
            </Link>
          </BreadcrumbLink>
          <BreadcrumbLink href="/individual-payment">
            <Link to="/individual-payment">{t('breadcrumb')}</Link>
          </BreadcrumbLink>
          {/* O aluno é segmento intermediário: como `BreadcrumbPage` ele não
              emitia o separador e a trilha terminava sem dizer em que seção o
              operador está. `asChild={false}` mantém texto simples com o ">". */}
          <BreadcrumbLink asChild={false}>{studentName}</BreadcrumbLink>
          <BreadcrumbPage>{t('parcels.title')}</BreadcrumbPage>
        </Breadcrumb>

        <div className="flex flex-wrap items-start justify-between gap-xs">
          <div className="flex flex-col gap-quark">
            <h1 className="ts-heading-lg text-ctx-content-title">{t('parcels.title')}</h1>
            {courseName && <p className="ts-paragraph-xs text-ctx-content-base">{courseName}</p>}
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-micro">
            {/* O tooltip existe principalmente para o estado desabilitado: sem ele,
                um botão inerte parece defeito da tela em vez de dado que faltou no
                contrato. */}
            <Tooltip
              title={
                canEditPerson
                  ? t('parcels.headerActions.editPersonHint')
                  : t('parcels.headerActions.editPersonUnavailable')
              }
            >
              <Button
                hierarchy="secondary"
                size="md"
                leadingIcon={UserIcon}
                disabled={!canEditPerson}
                onClick={() => onAction('editPerson')}
              >
                {t('parcels.headerActions.editPerson')}
              </Button>
            </Tooltip>
            {isVisible('editPayment') && (
              <Button
                hierarchy="secondary"
                size="md"
                leadingIcon={PencilSquareIcon}
                onClick={() => onAction('editPayment')}
              >
                {t('parcels.headerActions.editPayment')}
              </Button>
            )}
            {isVisible('downloadBooklet') && (
              <Button
                hierarchy="secondary"
                size="md"
                leadingIcon={ArrowDownTrayIcon}
                onClick={() => onAction('downloadBooklet')}
              >
                {t('parcels.headerActions.downloadBooklet')}
              </Button>
            )}
            {isVisible('addParcel') && (
              <Button
                hierarchy="primary"
                size="md"
                leadingIcon={PlusIcon}
                onClick={() => onAction('addParcel')}
              >
                {t('parcels.headerActions.addParcel')}
              </Button>
            )}

            {/* `modal={false}`: três itens deste menu abrem o modal de
                confirmação, e um DropdownMenu modal do Radix aplica
                `pointer-events: none` no `body` — abrir o dialog no mesmo tique
                faz a limpeza dos dois se atropelar e a página fica inerte depois
                de fechar o modal.

                Sem nenhuma ação restante o gatilho não é renderizado: um menu
                que abre vazio é pior que a ausência do botão. */}
            {moreActions.length > 0 && (
              <Dropdown
                modal={false}
                align="end"
                trigger={
                  <IconButton
                    ariaLabel={t('parcels.headerActions.more')}
                    hierarchy="secondary"
                    size="md"
                    icon={EllipsisVerticalIcon}
                  />
                }
              >
                <DropdownTitle custom={{ content: 'text-nowrap' }}>
                  {t('parcels.headerActions.more')}
                </DropdownTitle>
                {moreActions.map(item => (
                  <DropdownItem
                    key={item.action}
                    disabled={disabledActions.includes(item.action)}
                    onSelect={() => onAction(item.action)}
                    {...('isDestructive' in item ? { status: 'destructive' as const } : {})}
                  >
                    <DropdownItemContent
                      custom={{ content: 'text-nowrap' }}
                      leadingIcon={item.icon}
                    >
                      {t(item.labelKey)}
                    </DropdownItemContent>
                  </DropdownItem>
                ))}
              </Dropdown>
            )}
          </div>
        </div>
      </div>

      {children}
    </div>
  );
}
